import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { DiversityTrendPoint } from '@/hooks/useDiversityAnalytics';

interface DiversityTrendChartProps {
  data: DiversityTrendPoint[] | undefined;
  isLoading: boolean;
}

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

function formatCurrencyShort(value: number): string {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}k`;
  }
  return `$${value.toFixed(0)}`;
}

function formatPercentage(value: number): string {
  return `${value.toFixed(1)}%`;
}

export function DiversityTrendChart({ data, isLoading }: DiversityTrendChartProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-72" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Diversity Spend Trend</CardTitle>
          <CardDescription>
            Track diversity spend performance over time
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex h-[300px] items-center justify-center text-muted-foreground">
            No trend data available for the selected period
          </div>
        </CardContent>
      </Card>
    );
  }

  // Prepare chart data
  const chartData = data.map(point => ({
    period: point.period,
    diverseSpend: point.diverseSpendCents / 100,
    diverseSpendCents: point.diverseSpendCents,
    totalSpend: point.totalSpendCents / 100,
    totalSpendCents: point.totalSpendCents,
    diversePercentage: point.diversePercentage,
    diverseJobs: point.diverseJobCount,
    totalJobs: point.totalJobCount,
  }));

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="rounded-lg border bg-background p-3 shadow-lg">
          <p className="font-medium">{label}</p>
          <div className="mt-2 space-y-1 text-sm">
            <p className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-emerald-500" />
              Diversity Spend: <span className="font-medium">{formatCurrency(data.diverseSpendCents)}</span>
            </p>
            <p className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-blue-500" />
              Diversity %: <span className="font-medium">{formatPercentage(data.diversePercentage)}</span>
            </p>
            <p className="text-muted-foreground">
              {data.diverseJobs} of {data.totalJobs} jobs ({formatCurrency(data.totalSpendCents)} total)
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  // Calculate max values for Y axes
  const maxSpend = Math.max(...chartData.map(d => d.diverseSpend));
  const maxPercentage = Math.max(...chartData.map(d => d.diversePercentage), 100);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Diversity Spend Trend</CardTitle>
        <CardDescription>
          Track diversity spend performance over time
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <defs>
                <linearGradient id="diverseSpendGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(152, 69%, 45%)" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="hsl(152, 69%, 45%)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="period"
                tick={{ fontSize: 12 }}
                tickLine={false}
              />
              <YAxis
                yAxisId="left"
                tickFormatter={formatCurrencyShort}
                tick={{ fontSize: 12 }}
                tickLine={false}
                axisLine={false}
                domain={[0, maxSpend * 1.1]}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tickFormatter={(v) => `${v}%`}
                tick={{ fontSize: 12 }}
                tickLine={false}
                axisLine={false}
                domain={[0, Math.min(maxPercentage * 1.2, 100)]}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                wrapperStyle={{ paddingTop: '20px' }}
                formatter={(value) => {
                  if (value === 'diverseSpend') return 'Diversity Spend ($)';
                  if (value === 'diversePercentage') return 'Diversity %';
                  return value;
                }}
              />
              <Area
                yAxisId="left"
                type="monotone"
                dataKey="diverseSpend"
                stroke="hsl(152, 69%, 45%)"
                strokeWidth={2}
                fill="url(#diverseSpendGradient)"
                name="diverseSpend"
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="diversePercentage"
                stroke="hsl(217, 91%, 60%)"
                strokeWidth={2}
                dot={{ fill: 'hsl(217, 91%, 60%)', strokeWidth: 0, r: 4 }}
                activeDot={{ r: 6 }}
                name="diversePercentage"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
